import {useEffect, useCallback} from 'react'
import {
    watchOnlineStatus,
    getSessionJSONItem,
    clearSessionJSONItem
} from '@salesforce/retail-react-app/app/utils/utils'
import {useUpdateShopperContext} from '../../../hooks/use-update-shopper-context'
import {
    sendUserSession,
    sendLoginEvent,
    sendSignUpEvent,
    sendSignUpEventVerification,
    consumePendingSignUpVerification,
    trackGtmPathname,
    initViewItemListInteractionTracking,
    normalizeSignUpMethod
} from '../../../utils/gtm-utils'

/* ------------------------------------------------------------------ */
/*  Internal sub-hooks – grouped by concern for readability           */
/* ------------------------------------------------------------------ */

function useBasketSyncEffects({
    basket,
    customer,
    currency,
    isMaintenanceEnabled,
    updateBasket,
    updateCustomerForBasket
}) {
    useEffect(() => {
        if (isMaintenanceEnabled) return
        if (basket?.currency && basket?.currency !== currency) {
            updateBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {currency}
            })
        }
    }, [basket?.currency, isMaintenanceEnabled])

    useEffect(() => {
        if (isMaintenanceEnabled) return
        if (
            basket &&
            customer?.isRegistered &&
            customer?.email &&
            customer?.email !== basket?.customerInfo?.email
        ) {
            updateCustomerForBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {
                    email: customer.email
                }
            })
        }
    }, [customer?.isRegistered, customer?.email, basket?.customerInfo?.email, isMaintenanceEnabled])
}

function useGtmTrackingEffects({customer, location, isDidomiReady, locale, brandId}) {
    const searchParams = new URLSearchParams(location.search)
    const searchQuery = searchParams.get('q') || ''
    const smartSearch = searchParams.get('smartSearch') || ''
    useEffect(() => {
        if (customer?.email) {
            let method = 'email_password'

            const fromSocial = getSessionJSONItem('from-social')
            if (fromSocial) {
                // Prefer provider name stored as the session value (google|apple|facebook).
                const socialMethod = normalizeSignUpMethod(
                    typeof fromSocial === 'string' && fromSocial !== 'true' ? fromSocial : null
                )
                if (socialMethod) method = socialMethod
                clearSessionJSONItem('from-social')
            }

            // They may have verified by clicking the emailed link instead of logging in
            // through the form - this runs on every page, so it still catches that.
            const pendingSignUp = consumePendingSignUpVerification()

            if (pendingSignUp) {
                sendSignUpEventVerification(pendingSignUp.interactionLocation, pendingSignUp.method)
            } else if (getSessionJSONItem('from-registration')) {
                clearSessionJSONItem('from-registration')
                sendSignUpEvent(customer, method)
            } else if (getSessionJSONItem('from-login')) {
                clearSessionJSONItem('from-login')
                sendLoginEvent(customer, method)
            }
        }
    }, [customer?.email])

    useEffect(() => {
        if (!isDidomiReady) return
        if (!customer?.customerType) return
        if (!brandId || !locale?.id) return
        if (customer.isRegistered && !customer.email) return

        sendUserSession(customer, location, locale, brandId)
    }, [
        location.pathname,
        searchQuery,
        smartSearch,
        isDidomiReady,
        customer?.customerType,
        customer?.isRegistered,
        customer?.email,
        locale?.id,
        locale?.countryCode,
        brandId
    ])

    useEffect(() => {
        trackGtmPathname(location.pathname)
    }, [location.pathname])

    useEffect(() => initViewItemListInteractionTracking(), [])
}

function useOnlineStatusEffect({setIsOnline}) {
    useEffect(() => {
        watchOnlineStatus((isOnline) => {
            setIsOnline(isOnline)
        })
    }, [])
}

function useBrandClassEffect({brandId}) {
    useEffect(() => {
        const root = document.documentElement
        root.classList.remove('is-garage', 'is-dynamite')
        if (brandId) {
            root.classList.add(`is-${brandId}`)
        }
    }, [brandId])
}

function useDrawerAutoCloseEffect({location, onDrawerClose}) {
    useEffect(() => {
        onDrawerClose()
    }, [location])
}

function useSignifydEffect({basket, setSignifydReady}) {
    useEffect(() => {
        if (basket?.c_SignifydOrderSessionId) {
            setSignifydReady(true)
        }
    }, [basket?.c_SignifydOrderSessionId])
}

/* ------------------------------------------------------------------ */
/*  Public hook                                                        */
/* ------------------------------------------------------------------ */

export const useAppEffects = ({
    basket,
    customer,
    currency,
    location,
    isMaintenanceEnabled,
    brandId,
    onDrawerClose,
    setIsOnline,
    setSignifydReady,
    isDidomiReady,
    activeData,
    site,
    locale,
    updateBasket,
    updateCustomerForBasket
}) => {
    useBasketSyncEffects({basket, customer, currency, isMaintenanceEnabled, updateBasket, updateCustomerForBasket})
    useGtmTrackingEffects({customer, location, isDidomiReady, locale, brandId})
    useOnlineStatusEffect({setIsOnline})
    useBrandClassEffect({brandId})
    useDrawerAutoCloseEffect({location, onDrawerClose})
    useSignifydEffect({basket, setSignifydReady})
    useUpdateShopperContext()

    const trackPage = useCallback(() => {
        activeData.trackPage(site.id, locale.id, currency)
    }, [activeData, site.id, locale.id, currency])

    // Fire page tracking on route change
    useEffect(() => {
        trackPage()
    }, [location.pathname, isDidomiReady, customer?.email])

    return {trackPage}
}
